import { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Stack,
  ImageList,
  ImageListItem,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import LegalHero from './legal/LegalHero'

const MotionBox = motion(Box)
const MotionImageListItem = motion(ImageListItem)

// Gallery categories
const categories = [
  { id: 'all', label: 'All' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'dining', label: 'Dining' },
  { id: 'exterior', label: 'Exterior' },
  { id: 'amenities', label: 'Amenities' },
]

// Gallery images with metadata
const galleryItems = [
  {
    img: '/images/hotel-front.jpeg',
    title: 'Hotel Front View',
    category: 'exterior',
    cols: 2,
    featured: true,
  },
  {
    img: '/images/rooms/deluxe-ac/room-1.jpeg',
    title: 'Deluxe AC Room',
    category: 'rooms',
  },
  {
    img: '/images/rooms/deluxe-ac/room-2.jpeg',
    title: 'Deluxe AC Room View',
    category: 'rooms',
  },
  {
    img: '/images/rooms/deluxe-ac/bathroom.jpeg',
    title: 'Deluxe AC Bathroom',
    category: 'rooms',
  },
  {
    img: '/images/rooms/super-deluxe/room-1.jpeg',
    title: 'Super Deluxe Room',
    category: 'rooms',
    featured: true,
  },
  {
    img: '/images/rooms/super-deluxe/room-2.jpeg',
    title: 'Super Deluxe Room View',
    category: 'rooms',
  },
  {
    img: '/images/rooms/super-deluxe/bathroom.jpeg',
    title: 'Super Deluxe Bathroom',
    category: 'rooms',
  },
  {
    img: '/images/rooms/family-suite/room-1.jpeg',
    title: 'Family Suite',
    category: 'rooms',
    cols: 2,
    featured: true,
  },
  {
    img: '/images/rooms/family-suite/room-2.jpeg',
    title: 'Family Suite Living Area',
    category: 'rooms',
  },
  {
    img: '/images/rooms/family-suite/bathroom-1.jpeg',
    title: 'Family Suite Bathroom',
    category: 'rooms',
  },
]

const Gallery = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const [activeCategory, setActiveCategory] = useState('all')

  // Filter images based on active category
  const filteredImages = galleryItems.filter(
    item => activeCategory === 'all' || item.category === activeCategory
  )

  return (
    <Box>
      <LegalHero
        title="Our Gallery"
        subtitle="Take a visual journey through our luxurious accommodations and facilities"
      />

      <Container maxWidth="xl">
        {/* Filter Buttons */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{
            py: 6,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={activeCategory === category.id ? 'contained' : 'outlined'}
              onClick={() => setActiveCategory(category.id)}
              sx={{
                minWidth: 100,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: activeCategory === category.id ? 600 : 400,
                borderColor: activeCategory === category.id ? 'primary.main' : alpha(theme.palette.primary.main, 0.3),
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: activeCategory === category.id ? 'primary.main' : alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              {category.label}
            </Button>
          ))}
        </Stack>

        {/* Gallery Grid */}
        <Box sx={{ pb: 8 }}>
          <ImageList
            variant="quilted"
            cols={isMobile ? 1 : isTablet ? 2 : 3}
            gap={24}
            sx={{
              m: 0,
              '& .MuiImageListItem-root': {
                overflow: 'hidden',
                borderRadius: 2,
              },
            }}
          >
            <AnimatePresence>
              {filteredImages.map((item, index) => (
                <MotionImageListItem
                  key={item.img}
                  cols={isMobile ? 1 : item.cols || 1}
                  rows={item.featured ? 2 : 1}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover img': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <img
                    src={item.img}
                    alt={item.title}
                    loading="lazy"
                    style={{
                      borderRadius: 8,
                      transition: 'transform 0.3s ease',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      p: 2,
                      transform: 'translateY(100%)',
                      transition: 'transform 0.3s ease',
                      '.MuiImageListItem-root:hover &': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    <Typography variant="subtitle1">{item.title}</Typography>
                  </Box>
                </MotionImageListItem>
              ))}
            </AnimatePresence>
          </ImageList>
        </Box>
      </Container>
    </Box>
  )
}

export default Gallery 